import { Envelope, GoogleChromeLogo, type Icon } from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import RadioButton from "common/components/ui/RadioButton";
import { Tag } from "common/components/ui/Tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useExtensionLogin from "common/hooks/useExtensionLogin";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface LoginMethod {
  type: "email" | "extension";
  icon: Icon;
  title: string;
  subtitle: string;
}

interface LoginMethodSelectionProps {
  selectedLoginMethod: "extension" | "email";
  setSelectedLoginMethod: (loginMethod: "extension" | "email") => void;
}

export default function LoginMethodSelection({
  selectedLoginMethod,
  setSelectedLoginMethod,
}: LoginMethodSelectionProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { isExtensionSupported, extensionName, isExtensionLoginVisible } =
    useExtensionLogin();

  const loginMethods: LoginMethod[] = [
    {
      type: "email",
      icon: Envelope,
      title: "Email and password",
      subtitle: "Login your account via email & password",
    },
  ];

  if (isExtensionLoginVisible) {
    loginMethods.unshift({
      type: "extension",
      icon: GoogleChromeLogo,
      title: `${extensionName} extension`,
      subtitle: `${!isDesktop ? "Desktop only: " : ""}Connect to ${getWhitelabelCustomization() ? "the dashboard" : "Aimfox"} using an existing LinkedIn login session`,
    });
  }

  return (
    <>
      {loginMethods.map((option) => {
        const { title, subtitle, icon: OptionIcon, type } = option;
        const isOptionDisabled = type === "extension" && !isExtensionSupported;

        const buttonComponent = (
          <button
            key={type}
            type="button"
            aria-label={title}
            className={clsx([
              "flex cursor-pointer items-center justify-between gap-4 rounded-3xl border p-4",
              selectedLoginMethod === type
                ? "border-purple-600 bg-purple-50"
                : "border-black-200",
            ])}
            disabled={isOptionDisabled}
            onClick={() => setSelectedLoginMethod(type)}
          >
            <div className="flex flex-row items-center gap-2">
              <OptionIcon
                size={32}
                className={clsx(isOptionDisabled && "text-black-400")}
              />

              <div className="flex flex-col text-start">
                <div className="flex flex-row items-center gap-x-2">
                  <p
                    className={clsx(
                      "text-button-sm",
                      isOptionDisabled && "text-black-400",
                    )}
                  >
                    {title}
                  </p>

                  <RenderIf condition={type === "extension" && isDesktop}>
                    <Tag
                      variant={isOptionDisabled ? "primary-black" : "primary"}
                      className={clsx(isOptionDisabled && "bg-black-400")}
                    >
                      Recommended
                    </Tag>
                  </RenderIf>
                </div>

                <span className="text-xs text-black-500">{subtitle}</span>
              </div>
            </div>

            <RadioButton
              id={type}
              className="mt-1"
              variant="purple"
              checked={selectedLoginMethod === type}
              isStyleOnly
            />
          </button>
        );

        if (isOptionDisabled) {
          return (
            <Tooltip key={type}>
              <TooltipTrigger asChild>{buttonComponent}</TooltipTrigger>

              <TooltipContent>
                <p>
                  Your browser does not support the {extensionName} extension.
                  Please download Google Chrome to use this feature
                </p>
              </TooltipContent>
            </Tooltip>
          );
        }

        return buttonComponent;
      })}
    </>
  );
}
